import { memo } from "react";
import "../../reportDashboard.scss";

// ── GC INDUSTRY BENCHMARK DATA ──────────────────────────────────────
// Sources: BLS SOII 2022 (pub. Nov 2023) · CPWR Construction Chart Book 6th Ed.
// (2023) · OSHA TRIR averages by NAICS (2022) · AGC Constructor Safety
// Excellence data 2022-23 · National Safety Council Injury Facts 2023.
const GC_INDUSTRY = {
  label: "General Contractors (NAICS 2360)",
  subtitle: "BLS SOII 2022 · OSHA · CPWR",
  trir: {
    industry: 2.5,
    top25: 1.4,
    top10: 0.8,
    description: "Total Recordable Incident Rate (per 200k hrs)",
  },
  dart: {
    industry: 1.4,
    top25: 0.7,
    top10: 0.4,
    description: "Days Away, Restricted or Transferred Rate",
  },
  ltir: {
    industry: 0.9,
    top25: 0.4,
    top10: 0.2,
    description: "Lost Time Incident Rate (per 200k hrs)",
  },
  nearMiss: {
    industry: 12.0,
    top25: 25.0,
    top10: 40.0,
    higherIsBetter: true,
    description:
      "Near-miss reports per 100 workers — higher = better safety culture",
  },
  credComp: {
    industry: 78.0,
    top25: 88.0,
    top10: 96.0,
    higherIsBetter: true,
    description: "% of credentials not currently expired (see note: distinct from the Overview Safety Score's Credential Compliance dimension, which also weighs Critical/High severity)",
  },
  inspFreq: {
    industry: 1.2,
    top25: 2.5,
    top10: 4.0,
    higherIsBetter: true,
    description: "Safety inspections per active project per month",
  },
  corrRate: {
    industry: 14.0,
    top25: 8.0,
    top10: 4.0,
    description: "Corrective finding rate (% of all inspection items)",
  },
  caseClose: {
    industry: 68.0,
    top25: 85.0,
    top10: 95.0,
    higherIsBetter: true,
    description: "Case closure rate (% of cases closed within 30 days)",
  },
  fatal: {
    industry: 9.4,
    top25: 4.0,
    top10: 1.5,
    description: "Fatality rate per 100,000 workers (BLS 2022)",
  },
  trainingHrs: {
    industry: 14.0,
    top25: 24.0,
    top10: 40.0,
    higherIsBetter: true,
    description: "Safety training hours per worker per year",
  },
};

// Residential home builders — same sources, different published rates
// (BLS SOII 2022 · NAHB 2023 · CPWR). Selected instead of GC_INDUSTRY when
// the org's own industry (Organisation.IndustryTypeId) is residential/
// home-building — never by client name.
const HOMEBUILDER_INDUSTRY = {
  label: "Residential Home Builders (NAICS 2361)",
  subtitle: "BLS SOII 2022 · NAHB 2023 · CPWR",
  trir: { industry: 3.1, top25: 1.8, top10: 1.0, description: "Total Recordable Incident Rate (per 200k hrs)" },
  dart: { industry: 1.8, top25: 0.9, top10: 0.5, description: "Days Away, Restricted or Transferred Rate" },
  ltir: { industry: 1.1, top25: 0.5, top10: 0.3, description: "Lost Time Incident Rate (per 200k hrs)" },
  nearMiss: {
    industry: 8.0,
    top25: 18.0,
    top10: 30.0,
    higherIsBetter: true,
    description: "Near-miss reports per 100 workers — higher = better safety culture",
  },
  credComp: { industry: 72.0, top25: 84.0, top10: 93.0, higherIsBetter: true, description: "% of credentials not currently expired (see note: distinct from the Overview Safety Score's Credential Compliance dimension, which also weighs Critical/High severity)" },
  inspFreq: { industry: 0.8, top25: 1.8, top10: 3.2, higherIsBetter: true, description: "Safety inspections per active project per month" },
  corrRate: { industry: 16.0, top25: 10.0, top10: 5.0, description: "Corrective finding rate (% of all inspection items)" },
  caseClose: { industry: 58.0, top25: 78.0, top10: 92.0, higherIsBetter: true, description: "Case closure rate (% of cases closed within 30 days)" },
  fatal: { industry: 11.2, top25: 5.0, top10: 2.0, description: "Fatality rate per 100,000 workers (BLS 2022)" },
  trainingHrs: { industry: 10.0, top25: 20.0, top10: 35.0, higherIsBetter: true, description: "Safety training hours per worker per year" },
};

const isResidentialIndustry = (industryType) =>
  /resident|home\s*build/i.test(industryType || "");
const isSpecialtyIndustry = (industryType) =>
  /specialty/i.test(industryType || "");

// Inclusive month span between two yyyy-MM-dd dates, e.g. 2026-01-15..2026-03-01 = 3.
function monthsBetween(fromIso, toIso) {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  return (
    (to.getFullYear() - from.getFullYear()) * 12 +
    (to.getMonth() - from.getMonth()) +
    1
  );
}

// F20: Inspection Frequency divided a date-range-scoped inspection count by a HARDCODED
// 6 -- correct only when a client happened to select a 6-month range, and silently wrong
// for every preset (7/14/30/90 days) and for All Time, where it understated the true
// monthly pace by roughly 6x. Derives the real span instead: an explicit custom/preset
// range uses its own from/to; All Time has no range to read, so it falls back to the
// distinct months actually present in the trend data (the same monthly buckets the
// Overview's own trend chart groups by), which is the best available proxy for "how much
// calendar time this client's data spans." Floored at 1 so a same-month range, or a trend
// with a single bucket, never divides by zero.
function inspectionMonthSpan(dateRange, trend) {
  if (dateRange?.from && dateRange?.to) {
    return Math.max(1, monthsBetween(dateRange.from, dateRange.to));
  }
  const distinctMonths = new Set((trend || []).map((t) => t.month)).size;
  return Math.max(1, distinctMonths);
}

// Percentile bands carry a semantic tone, never a colour — reportDashboard.scss
// resolves is-kudos / is-warning / is-danger against scss/_variables.scss.
//
// clientVal === null means "nothing to measure" (F21): the metric's own denominator was
// zero (no observations logged, no credentials on file, no cases opened). Previously a
// zero-cases org read caseCloseVal as 100% ("nothing open" read as "everything closed"),
// and a zero-credentials org read credCompVal as 100% the same way — both landing in
// "Top 10%". A zero-observations org hit the same defect from the corrRate side: 0%
// correctives is <= every "lower is better" threshold, so it too scored "Top 10%". None of
// these clients had done anything; the governing rule from the Safety Score rewrite
// applies here identically — a metric is either measured against a real denominator, or
// it is N/A and excluded, never scored as an extreme in either direction.
function getPercentile(clientVal, b, higherIsBetter) {
  if (clientVal === null) return { pct: null, label: "N/A", tone: "muted" };
  if (higherIsBetter) {
    if (clientVal >= b.top10)
      return { pct: 95, label: "Top 10%", tone: "kudos" };
    if (clientVal >= b.top25)
      return { pct: 80, label: "Top 25%", tone: "kudos" };
    if (clientVal >= b.industry)
      return { pct: 55, label: "Above Average", tone: "warning" };
    return { pct: 25, label: "Below Average", tone: "danger" };
  }
  if (clientVal <= b.top10) return { pct: 95, label: "Top 10%", tone: "kudos" };
  if (clientVal <= b.top25) return { pct: 80, label: "Top 25%", tone: "kudos" };
  if (clientVal <= b.industry)
    return { pct: 55, label: "Above Average", tone: "warning" };
  return { pct: 25, label: "Below Average", tone: "danger" };
}

function isPercentTitle(title) {
  return /%|Rate|Comp|Close/.test(title);
}

function BenchRow({ label, value, tone, fill }) {
  return (
    <div className="rdBench__row">
      <div className="rdBench__rowHead">
        <span className="rdHBar__label">{label}</span>
        <span className={`rdBench__rowValue is-${tone}`}>{value}</span>
      </div>
      <div className="rdBar">
        <div
          className={`rdBar__fill is-${tone}`}
          style={{ "--rd-fill": `${fill}%` }}
        />
      </div>
    </div>
  );
}

function BenchCard({ title, clientVal, clientLabel, b, higherIsBetter, note }) {
  const hib = higherIsBetter || false;
  const perc = getPercentile(clientVal, b, hib);
  // clientVal null (F21) must not reach the width arithmetic below: `clientVal * 1.1`
  // would silently coerce to 0, and parseFloat("N/A") is NaN, so the Client row's bar
  // would render with an invalid `--rd-fill: NaN%` custom property instead of failing
  // loudly. Guarded explicitly instead: the Client row is omitted altogether and a plain
  // "No data in this range" line takes its place, while the Industry/Top 25%/Top 10%
  // reference bars keep rendering for context.
  const hasClientData = clientVal !== null;
  const maxVal = hib
    ? Math.max(b.top10 * 1.15, (clientVal ?? 0) * 1.1)
    : Math.max(b.industry * 1.3, (clientVal ?? 0) * 1.1);
  const barWidth = (v) => Math.min(95, (v / maxVal) * 100).toFixed(1);
  const suffix = isPercentTitle(title) ? "%" : "";

  return (
    <div className="rdCard rdCard--flush">
      <div className="rdBench__cardHead">
        <div>
          <div className="rdBench__metric">{title}</div>
          <div className={`rdBench__value is-${perc.tone}`}>{clientLabel}</div>
          <div className="rdBench__description">{b.description}</div>
        </div>
        <span className={`rdPill rdPill--soft is-${perc.tone}`}>
          {perc.label}
        </span>
      </div>

      {hasClientData ? (
        <BenchRow
          label="Client"
          value={clientLabel}
          tone={perc.tone}
          fill={barWidth(parseFloat(clientLabel))}
        />
      ) : (
        <div className="rdEmpty">No data in this range</div>
      )}
      <BenchRow
        label="Industry Average"
        value={`${b.industry}${suffix}`}
        tone="muted"
        fill={barWidth(b.industry)}
      />
      <BenchRow
        label="Top 25%"
        value={`${b.top25}${suffix}`}
        tone="warning"
        fill={barWidth(b.top25)}
      />
      <BenchRow
        label="Top 10%"
        value={`${b.top10}${suffix}`}
        tone="kudos"
        fill={barWidth(b.top10)}
      />

      {note && <div className="rdBench__note">{note}</div>}
    </div>
  );
}

function radarPts(vals, size) {
  const cx = size / 2,
    cy = size / 2,
    r = size * 0.38;
  const n = vals.length;
  return vals
    .map((v, i) => {
      const angle = (i / n) * 2 * Math.PI - Math.PI / 2;
      const x = cx + r * v * Math.cos(angle);
      const y = cy + r * v * Math.sin(angle);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

// Axis names run up to 20 characters ("Credential Currency"), which overruns
// the padded viewBox on the left/right spokes — break them onto two lines so
// the widest word, not the whole name, sets how far a label reaches.
function labelLines(label) {
  const words = label.split(" ");
  if (words.length <= 2) return words;
  return [words.slice(0, 2).join(" "), words.slice(2).join(" ")];
}

function RadarAxis({ i, n, size, label }) {
  const cx = size / 2,
    cy = size / 2,
    r = size * 0.38;
  const angle = (i / n) * 2 * Math.PI - Math.PI / 2;
  const x1 = cx + r * Math.cos(angle),
    y1 = cy + r * Math.sin(angle);
  const lx = cx + (r + 18) * Math.cos(angle),
    ly = cy + (r + 18) * Math.sin(angle);
  const anchor = lx < cx - 5 ? "end" : lx > cx + 5 ? "start" : "middle";
  const lines = labelLines(label);
  return (
    <>
      <line
        className="rdBench__radarAxis"
        x1={cx}
        y1={cy}
        x2={x1.toFixed(1)}
        y2={y1.toFixed(1)}
        strokeWidth="1"
      />
      <text
        className="rdBench__axisLabel"
        x={lx.toFixed(1)}
        y={ly.toFixed(1)}
        textAnchor={anchor}
        dominantBaseline="middle"
      >
        {lines.map((line, li) => (
          <tspan
            key={line}
            x={lx.toFixed(1)}
            dy={li === 0 ? `${-0.55 * (lines.length - 1)}em` : "1.1em"}
          >
            {line}
          </tspan>
        ))}
      </text>
    </>
  );
}

function RadarChart({ clientVals, industryVals, axes }) {
  const sz = 200;
  const rings = [0.25, 0.5, 0.75, 1.0].map((rv, idx) => {
    const pts = Array.from({ length: 5 }, (_, i) => {
      const angle = (i / 5) * 2 * Math.PI - Math.PI / 2;
      const cx = sz / 2,
        cy = sz / 2,
        r = sz * 0.38;
      return `${(cx + r * rv * Math.cos(angle)).toFixed(1)},${(cy + r * rv * Math.sin(angle)).toFixed(1)}`;
    }).join(" ");
    return (
      <polygon
        className="rdBench__radarRing"
        key={idx}
        points={pts}
        strokeWidth="0.8"
      />
    );
  });

  return (
    // The polygon fills 0..sz; the label ring sits outside it, so the viewBox
    // is padded (60 each side, 20 top/bottom) instead of shrinking the chart.
    <svg
      viewBox={`-60 -20 ${sz + 120} ${sz + 40}`}
      width={sz + 120}
      height={sz + 40}
    >
      {rings}
      {axes.map((label, i) => (
        <RadarAxis key={label} i={i} n={5} size={sz} label={label} />
      ))}
      <polygon
        className="rdBench__radarIndustry"
        points={radarPts(industryVals, sz)}
        strokeWidth="1.5"
        strokeDasharray="4,3"
      />
      <polygon
        className="rdBench__radarClient"
        points={radarPts(clientVals, sz)}
        strokeWidth="2"
      />
    </svg>
  );
}

const BenchmarksSection = ({ data, dateRange }) => {
  const kpis = data.kpis || {};
  const clientName = data.client?.clientName || "This client";
  const industryType = data.client?.industryType || "";
  const bench = isResidentialIndustry(industryType) ? HOMEBUILDER_INDUSTRY : GC_INDUSTRY;

  // Denominator is kpis.typedItemsTotal, not kpis.totalItems: totalItems is the raw
  // all-status row count and includes rows whose ItemType is "Unknown" (an
  // unrecognized SafetyItemType value) -- the Corrective-count numerator can never
  // include those rows, so using totalItems as the denominator systematically
  // understated the rate. typedItemsTotal is the sum of exactly the four recognized
  // types (Kudos + Corrective + Action + Reprimand), so this rate's own numerator is
  // guaranteed to be a subset of its denominator's contributors.
  const totalObservations = kpis.typedItemsTotal || 0;
  const correctiveObservations = kpis.correctiveItemsTotal || 0;
  const totalCredentials = kpis.totalCredentials || 0;
  const expiredCredentials = kpis.expiredCredentials || 0;
  const totalCases = kpis.totalCases || 0;
  const closedCases = kpis.closedCases || 0;
  const totalInspections = kpis.totalInspections || 0;
  const activeProjects = kpis.activeProjects || 0;
  const nearMissCount = kpis.nearMissReports || 0;

  // F21: each of these used to fill in a value on a zero denominator that read as a
  // PERFECT score rather than "nothing to measure" -- 0 cases read as caseCloseVal=100%
  // ("nothing open" mistaken for "everything closed"), 0 credentials read as
  // credCompVal=100% the same way, and 0 observations read as corrRateVal=0%, which for a
  // lower-is-better metric is *also* a perfect score. All three landed in "Top 10%" for an
  // org that had done nothing. Each metric's value is now null on a zero denominator, and
  // getPercentile above turns that into an explicit N/A rather than an extreme.
  const corrRateVal = totalObservations > 0 ? (correctiveObservations / totalObservations) * 100 : null;
  const credCompVal = totalCredentials > 0 ? ((totalCredentials - expiredCredentials) / totalCredentials) * 100 : null;
  const caseCloseVal = totalCases > 0 ? (closedCases / totalCases) * 100 : null;
  // F20: was a hardcoded /6 regardless of the selected date range or how much history
  // exists; see inspectionMonthSpan's own comment. activeProjects === 0 is its own N/A
  // case too -- a client with no active projects has no denominator for "per project per
  // month," not a rock-bottom inspection rate.
  const monthSpan = inspectionMonthSpan(dateRange, data.trend);
  const inspFreqVal = activeProjects > 0 ? totalInspections / activeProjects / monthSpan : null;

  const fmtPct = (v) => (v === null ? "N/A" : `${v.toFixed(1)}%`);
  const client = {
    corrRate: {
      value: corrRateVal === null ? null : Number(corrRateVal.toFixed(1)),
      label: fmtPct(corrRateVal),
    },
    credComp: {
      value: credCompVal === null ? null : Number(credCompVal.toFixed(1)),
      label: fmtPct(credCompVal),
    },
    caseClose: {
      value: caseCloseVal === null ? null : Number(caseCloseVal.toFixed(1)),
      label: fmtPct(caseCloseVal),
    },
    inspFreq: {
      value: inspFreqVal === null ? null : Number(inspFreqVal.toFixed(1)),
      label: inspFreqVal === null ? "N/A" : `${inspFreqVal.toFixed(1)}/mo`,
    },
  };

  const corrPerc = getPercentile(client.corrRate.value, bench.corrRate, false);
  const credPerc = getPercentile(client.credComp.value, bench.credComp, true);
  const casePerc = getPercentile(client.caseClose.value, bench.caseClose, true);
  const inspPerc = getPercentile(client.inspFreq.value, bench.inspFreq, true);
  // Averaged over whichever metrics are actually measurable, same rescaling rule as the
  // Safety Score rewrite -- an N/A metric leaves the average entirely rather than being
  // treated as 0 (which would understate a client who is simply missing one data type)
  // or full marks (the original defect this section exists to fix).
  const measurablePercs = [corrPerc, credPerc, casePerc, inspPerc].filter((p) => p.pct !== null);
  const avgPerc = measurablePercs.length > 0
    ? Math.round(measurablePercs.reduce((a, p) => a + p.pct, 0) / measurablePercs.length)
    : null;

  let overallLabel, overallTone;
  if (avgPerc === null) {
    overallLabel = "Not enough data to benchmark";
    overallTone = "muted";
  } else if (avgPerc >= 85) {
    overallLabel = "Top 10% Performer";
    overallTone = "kudos";
  } else if (avgPerc >= 70) {
    overallLabel = "Top 25% Performer";
    overallTone = "kudos";
  } else if (avgPerc >= 50) {
    overallLabel = "Above Industry Average";
    overallTone = "warning";
  } else {
    overallLabel = "Below Industry Average";
    overallTone = "danger";
  }

  const corrNI =
    1 - Math.min(1, bench.corrRate.industry / (bench.corrRate.industry * 2));
  const credNI = Math.min(1, bench.credComp.industry / 100);
  const caseNI = Math.min(1, bench.caseClose.industry / 100);
  const inspNI = Math.min(1, bench.inspFreq.industry / bench.inspFreq.top10);
  const nmNI = 0.4;

  // Same N/A guard as the percentile cards, applied to the radar's own independent 0-1
  // normalisation. Without this, a null client value coerced to 0 in the arithmetic below
  // -- and for corrRate (lower-is-better) that 0 produced corrN = 1, plotting a client
  // with NO observations at the radar's most impressive point on that axis. When a metric
  // is N/A, its radar coordinate is set to the industry-average coordinate instead of a
  // fabricated extreme, so the client polygon simply tracks the industry line on that one
  // axis rather than asserting a performance that was never measured.
  const corrN =
    client.corrRate.value === null
      ? corrNI
      : 1 - Math.min(1, client.corrRate.value / (bench.corrRate.industry * 2));
  const credN = client.credComp.value === null ? credNI : Math.min(1, client.credComp.value / 100);
  const caseN = client.caseClose.value === null ? caseNI : Math.min(1, client.caseClose.value / 100);
  const inspN =
    client.inspFreq.value === null
      ? inspNI
      : Math.min(1, client.inspFreq.value / bench.inspFreq.top10);
  const nmN = Math.min(1, nearMissCount > 0 ? 0.7 : 0.3);

  const clientVals = [corrN, credN, caseN, inspN, nmN];
  const industryVals = [corrNI, credNI, caseNI, inspNI, nmNI];
  const axes = [
    "Corrective Control",
    "Credential Currency",
    "Case Closure",
    "Inspection Activity",
    "Near Miss Culture",
  ];

  const improvementTargets = [
    {
      metric: "Corrective Rate",
      current: `${client.corrRate.label}`,
      target: `${bench.corrRate.top25}%`,
      stretch: `${bench.corrRate.top10}%`,
      tip: "Increase inspection frequency and pre-task planning to catch hazards before they become findings.",
    },
    {
      metric: "Credential Currency Rate",
      current: `${client.credComp.label}`,
      target: `${bench.credComp.top25}%`,
      stretch: `${bench.credComp.top10}%`,
      tip: "Implement 90-day renewal alerts and assign a credential owner per division.",
    },
    {
      metric: "Case Closure",
      current: `${client.caseClose.label}`,
      target: `${bench.caseClose.top25}%`,
      stretch: `${bench.caseClose.top10}%`,
      tip: "Set a 30-day SLA for case closure. Assign investigating supervisor accountability.",
    },
    {
      metric: "Inspection Frequency",
      current: `${client.inspFreq.label}`,
      target: `${bench.inspFreq.top25}/mo`,
      stretch: `${bench.inspFreq.top10}/mo`,
      tip: "Aim for weekly site inspections on all active projects. Use the mobile app for field efficiency.",
    },
  ];

  const publishedRates = [
    { label: "Industry TRIR", metric: bench.trir },
    { label: "DART Rate", metric: bench.dart },
    { label: "Fatality Rate / 100k workers", metric: bench.fatal },
  ];

  return (
    <div>
      <div className="rdBench__header">
        <div>
          <div className="rdBench__title">{bench.label}</div>
          <div className="rdBench__subtitle">
            Data sources: {bench.subtitle}
          </div>
        </div>
        <div className="rdBench__standing">
          <div className="rdBench__standingLabel">Overall Standing</div>
          <div className={`rdBench__standingValue is-${overallTone}`}>
            {overallLabel}
          </div>
        </div>
      </div>

      <div className="rdNotice">
        <strong>Benchmark in use: {bench.label}.</strong> Every client on this
        dashboard is compared against the General Contractor industry set
        (sources: {bench.subtitle}), regardless of client type, so figures below
        are directly comparable across clients. <strong>Industry Avg</strong> =
        typical GC performance ·{" "}
        <strong className="rdValue is-warning">Top 25%</strong> = upper-quartile
        programs · <strong className="rdValue is-kudos">Top 10%</strong> =
        best-in-class (AGC award / OSHA VPP level). For rate metrics (TRIR,
        Corrective Rate, DART, Fatality Rate) <em>lower is better</em>; for
        culture metrics (Credential Currency, Case Closure, Inspection
        Frequency, Near-Miss Reporting) <em>higher is better</em>.
      </div>

      <div className="rdNotice">
        <strong>Data Sources &amp; Methodology</strong> &nbsp;·&nbsp; Bureau of
        Labor Statistics SOII (2022, pub. Nov 2023) &nbsp;·&nbsp; CPWR
        Construction Chart Book 6th Ed. (2023) &nbsp;·&nbsp; OSHA TRIR Industry
        Averages by NAICS (2022) &nbsp;·&nbsp; NAHB Eye on Housing Safety Report
        (2023) &nbsp;·&nbsp; AGC Constructor Safety Excellence Program (2022–23)
        &nbsp;·&nbsp; National Safety Council Injury Facts (2023) &nbsp;·&nbsp;{" "}
        <em>
          Top 10% / Top 25% thresholds derived from AGC award-winning programs
          and OSHA VPP participant averages.
        </em>
      </div>

      <div className="rdBench__split">
        <div className="rdCard rdCard--flush rdBench__radar">
          <div className="rdCard__title">Performance Radar</div>
          <RadarChart
            clientVals={clientVals}
            industryVals={industryVals}
            axes={axes}
          />
          <div className="rdLegend">
            <span className="rdLegend__item">
              <span className="rdSwatch rdSwatch--line is-accent" />
              Client
            </span>
            <span className="rdLegend__item">
              <span className="rdSwatch rdSwatch--line is-muted" />
              Industry Average
            </span>
          </div>
        </div>

        <div className="rdBench__side">
          <div className="rdCard">
            <div className="rdCard__title">Industry Context</div>
            <div className="rdBench__paragraph">
              {isResidentialIndustry(industryType) ? (
                <>
                  <b>Residential Construction context:</b> Home builders face
                  unique safety challenges including high subcontractor
                  turnover, dispersed multi-site operations, and weather
                  exposure. The NAHB reports the residential sector has a 17%
                  higher TRIR than commercial GC work. {clientName}&apos;s{" "}
                  {kpis.activeProjects || 0} active projects across multiple
                  divisions represent above-average portfolio complexity.
                </>
              ) : isSpecialtyIndustry(industryType) ? (
                <>
                  <b>Specialty Contractor context:</b> Specialty trade
                  contractors (NAICS 238x) typically see higher corrective
                  rates due to task-specific hazard exposure (confined space,
                  aerial work, high-rise). {clientName}&apos;s{" "}
                  {client.corrRate.label} corrective rate should be evaluated
                  against this specialized work profile rather than general GC
                  benchmarks.
                </>
              ) : (
                <>
                  <b>Commercial GC context:</b> General contractors managing
                  multi-division portfolios across industrial and commercial
                  projects typically benchmark against NAICS 2362.{" "}
                  {clientName}&apos;s near-miss reporting rate ({nearMissCount}{" "}
                  events) indicates an active reporting culture, which BLS and
                  NSC research correlates with 20–40% lower serious injury
                  rates.
                </>
              )}
            </div>
          </div>
          <div className="rdCard rdCard--flush">
            <div className="rdCard__title">Chart Legend</div>
            <div className="rdLegend">
              <span className="rdLegend__item">
                <span className="rdSwatch is-accent" /> Client value
              </span>
              <span className="rdLegend__item">
                <span className="rdSwatch is-muted" /> Industry average
              </span>
              <span className="rdLegend__item">
                <span className="rdSwatch is-warning" /> Top 25%
              </span>
              <span className="rdLegend__item">
                <span className="rdSwatch is-kudos" /> Top 10%
              </span>
            </div>
            <div className="rdBench__note">
              Bars show client value vs. industry benchmarks.{" "}
              <strong className="rdValue is-kudos">Green</strong> = Top 10%
              threshold &nbsp;
              <strong className="rdValue is-warning">Amber</strong> = Top 25%
              threshold &nbsp;
              <strong className="rdValue is-muted">Grey</strong> = Industry
              average
            </div>
          </div>
        </div>
      </div>

      <div className="rdSectionLabel">Key Safety Metrics vs. Industry</div>
      <div className="rdGrid rdGrid--3">
        <BenchCard
          title="Corrective Finding Rate"
          clientVal={client.corrRate.value}
          clientLabel={client.corrRate.label}
          b={bench.corrRate}
          higherIsBetter={false}
          note="Lower is better. Measures the % of inspection findings that are corrective vs. positive (kudos)."
        />
        <BenchCard
          title="Credential Currency Rate"
          clientVal={client.credComp.value}
          clientLabel={client.credComp.label}
          b={bench.credComp}
          higherIsBetter
          note="Higher is better. % of credentials that are not expired. Distinct from the Overview Safety Score's Credential Compliance dimension, which also weighs Critical/High-risk credentials, not only Expired."
        />
        <BenchCard
          title="Case Closure Rate"
          clientVal={client.caseClose.value}
          clientLabel={client.caseClose.label}
          b={bench.caseClose}
          higherIsBetter
          note="Higher is better. % of cases closed. Open cases signal unresolved safety events."
        />
      </div>

      <div className="rdGrid rdGrid--3">
        <BenchCard
          title="Inspection Frequency"
          clientVal={client.inspFreq.value}
          clientLabel={client.inspFreq.label}
          b={bench.inspFreq}
          higherIsBetter
          note="Higher is better. Safety inspections per active project per month."
        />
        <BenchCard
          title="Near-Miss Reporting"
          clientVal={nearMissCount}
          clientLabel={`${nearMissCount} events`}
          b={bench.nearMiss}
          higherIsBetter
          note={`Higher reporting = stronger safety culture. Industry average: ${bench.nearMiss.industry} per 100 workers.`}
        />
        <div className="rdCard rdCard--flush">
          {/* v26's card-title (HTML:2487) is a single-line "title left, hint
              right" row, matching every BenchCard beside it (.rdBench__metric,
              10px/uppercase) — not a stacked two-line 16px/700 heading, which
              made this the third card in the row visually outweigh its siblings. */}
          <div className="rdCard__title">
            <span>Published Industry Rates</span>
            <span className="rdCard__note">BLS 2022</span>
          </div>
          {publishedRates.map(({ label, metric }) => (
            <div className="rdBench__published" key={label}>
              <div className="rdBench__publishedHead">
                <span className="rdBench__publishedLabel">{label}</span>
                <span className="rdBench__publishedValue">
                  {metric.industry}
                </span>
              </div>
              <div className="rdBench__description">{metric.description}</div>
              <div className="rdBench__publishedTiers">
                <span className="rdValue is-warning">
                  Top 25%: {metric.top25}
                </span>
                <span className="rdValue is-kudos">
                  Top 10%: {metric.top10}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rdSectionLabel">
        Improvement Targets to Reach Next Benchmark Tier
      </div>
      {/* v26 (HTML:2517) wraps the whole 4-column grid in one enclosing card,
          with each target box as a nested surface2 panel — without it the four
          boxes float loose and don't read as one bounded module. */}
      <div className="rdCard">
        <div className="rdGrid rdGrid--4">
          {improvementTargets.map((t) => (
            <div className="rdBench__target" key={t.metric}>
              <div className="rdBench__targetMetric">{t.metric}</div>
              <div className="rdBench__targetCurrent">
                <span className="rdBench__targetValue">{t.current}</span>
                <span className="rdBench__targetCaption">current</span>
              </div>
              <div className="rdBench__targetTier">
                <span className="rdValue is-warning">▶ Top 25%:</span> {t.target}
              </div>
              <div className="rdBench__targetTier">
                <span className="rdValue is-kudos">▶ Top 10%:</span> {t.stretch}
              </div>
              <div className="rdBench__targetTip">{t.tip}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(BenchmarksSection);
